/**
 * Story 034 — small fetch helper for auth/v1 mutations from inside the
 * lyse module.
 *
 * The `@aeriosiq/api-client` axios instance is hard-wired to the LYSE base
 * URL (`/api/lyse/v1`). The preferences endpoints live under
 * `/api/auth/v1/me/preferences/lyse`, so we follow the same pattern
 * `apps/web/src/features/account/queries/authFetch.ts` uses for the
 * account-page mutations. Duplicated rather than imported because
 * apps/lyse cannot pull from apps/web.
 */
import {
  fetchWithRefresh,
  getAuthBaseUrl,
  useAuthStore,
} from "@aeriosiq/auth-client";

export interface AuthFetchError extends Error {
  status: number;
  body: unknown;
}

interface AuthFetchOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
}

function authHeaders(): Headers {
  const session = useAuthStore.getState().session;
  const tenant = useAuthStore.getState().tenant;
  const headers = new Headers();
  if (session?.accessToken) {
    headers.set("Authorization", `Bearer ${session.accessToken}`);
  }
  if (tenant?.tenantSlug) {
    headers.set("X-Tenant-ID", tenant.tenantSlug);
  }
  return headers;
}

export async function authFetch<T>(
  path: string,
  opts: AuthFetchOptions = {},
): Promise<T> {
  const headers = authHeaders();
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    body = JSON.stringify(opts.body);
    headers.set("Content-Type", "application/json");
  }
  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
  };
  if (body !== undefined) {
    init.body = body;
  }
  // SB6-AUTH-REFRESH-01: silent refresh-on-401 + replay (see the
  // shared helper in @aeriosiq/auth-client).
  const res = await fetchWithRefresh(`${getAuthBaseUrl()}${path}`, init);
  if (!res.ok) {
    let parsedBody: unknown = null;
    try {
      parsedBody = await res.json();
    } catch {
      // ignore — non-JSON error bodies still surface the status
    }
    const err = new Error(
      `auth fetch failed: ${path} → ${String(res.status)}`,
    ) as AuthFetchError;
    err.status = res.status;
    err.body = parsedBody;
    throw err;
  }
  if (res.status === 204) {
    return undefined as unknown as T;
  }
  const text = await res.text();
  if (text === "") return undefined as unknown as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined as unknown as T;
  }
}
